/**
 * Text Field Type
 *
 * Provides configuration and renderers for text fields:
 * - Read-only column renderer
 * - Editable column renderer
 * - Form field renderer (with optional auto-generate button)
 */

import { memo, useCallback } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { TextInput } from 'components';
import { TableTooltip, SharedEditableCell, useEditableContextOptional } from '../../components';
import { Text } from 'components/text';
import { CellInput } from 'features/misc/table';
import { CellContainer, TruncatedText } from '../../styles';
import { toast } from 'react-toastify';
import { t } from '@lingui/macro';
import type {
  FieldTypeConfig,
  FormFieldProps,
  ColumnRenderer,
  RenderOptions,
  CustomColumn,
  EditableState,
} from '../../types';
import { clearEditOnSuccess } from '../inlineEditUtils';
import { validateFieldValue } from '../../validation';
import { FieldError, shouldShowFieldError } from './fieldError';
import { L } from './columnWidths';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { Box, IconButton } from '@mui/material';
import { smIconButton } from 'components/button/IconButton';
import { AutoGenerateRow } from '../../styles/modalStyles';
import { ClearFieldButton, ClearFieldFocusContainer } from '../../components/ClearFieldButton';
import { ClearedFieldIndicator } from '../../components/ClearedFieldIndicator';
import { getBulkEditClearState } from '../../hooks/useBulkEditClear';

// ============================================================================
// Editable Column Renderer
// ============================================================================

// Import shared cell value utility
import { getCellStringValue } from './cellValue';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Memoized read-only text cell component
 */
const TextReadOnlyCell = memo(function TextReadOnlyCell({ value }: { value: string }) {
  return (
    <TableTooltip title={value}>
      <CellContainer>
        <TruncatedText>{value}</TruncatedText>
      </CellContainer>
    </TableTooltip>
  );
});

/**
 * Text read-only renderer - displays text in a cell container with ellipsis for overflow
 * Uses getValue() to support nested accessors
 * Shows full text in tooltip on hover
 */
export const textReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    const value = cellContext.getValue();
    const displayValue = String(value ?? '');

    return <TextReadOnlyCell value={displayValue} />;
  },
  tooltipRender: (cell: CellContext<T, unknown>) => {
    const value = cell.getValue();
    return String(value ?? '');
  },
});

// ============================================================================
// Form Field Renderer
// ============================================================================

/**
 * Map field keys to their generator setting names
 * Used to determine which fields should show the auto-generate button
 */
const generatorFieldMap: Record<string, string> = {
  name: 'isNameAutoGenerated',
  userDefinedId: 'isNumberAutoGenerated',
};

/**
 * Render a text input field for forms
 * Automatically shows generate button below input when generatorSettings indicates the field is auto-generated
 */
export const renderTextField = ({
  col,
  value,
  valueForValidation,
  onChange,
  isFirst,
  disabled,
  inputRef,
  onEnterPress,
  generatorSettings,
  bulkEditContext,
  validationContext,
}: FormFieldProps): React.ReactNode => {
  // Check if this field has auto-generation enabled based on settings
  const settingKey = generatorFieldMap[col.key];
  const isAutoGenerated = !!(settingKey && generatorSettings?.[settingKey]);

  // Auto-generated field: disabled input with generate button below
  if (isAutoGenerated) {
    const handleGenerate = () => {
      // TODO: Call generate API and update value via onChange
      // onChange(col.key, generatedValue);
    };

    return (
      <Box key={col.key}>
        <TextInput
          label={col.label}
          placeholder="Auto-generate"
          fullWidth
          disabled
          value={(value as string) || ''}
          labelSeparate
          variant="outlined"
          InputProps={{ readOnly: true }}
        />
        <AutoGenerateRow disabled={disabled} onClick={disabled ? undefined : handleGenerate}>
          <IconButton
            onClick={e => {
              e.stopPropagation();
              if (!disabled) handleGenerate();
            }}
            disabled={disabled}
            sx={{ ...smIconButton, color: 'text.secondary' }}
          >
            <ArrowsClockwise size={14} />
          </IconButton>
          <Text variant="bodySm" color="secondary">
            Auto-generate
          </Text>
        </AutoGenerateRow>
      </Box>
    );
  }

  // Regular text input (non-auto-generated)
  const emptyDisplayNode = (col as { emptyDisplayNode?: React.ReactNode }).emptyDisplayNode;
  const valueEmpty = value == null || value === '';
  const showEmptyChip = valueEmpty && emptyDisplayNode;

  // Use Zod validation to get error message (valueForValidation for bulk-edit "keep current")
  const valueToValidate = valueForValidation !== undefined ? valueForValidation : value;
  const validation = validateFieldValue({ ...col, type: 'text' }, valueToValidate);
  const errorMessage = !validation.success ? validation.error : undefined;
  const showError = shouldShowFieldError(col.key, validationContext);

  const {
    isBulkEdit,
    isCleared,
    showClearButton,
    handleBulkEditChange,
    handleClear,
    handleUndoClear,
  } = getBulkEditClearState({
    value,
    valueForValidation,
    valueEmpty,
    colKey: col.key,
    required: (col as { required?: boolean }).required,
    onChange,
    bulkEditContext,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(col.key, handleBulkEditChange(e.target.value));
    validationContext?.onFieldTouched?.(col.key);
  };

  if (isBulkEdit && isCleared) {
    return (
      <Box key={col.key}>
        <ClearedFieldIndicator fieldName={col.label} onClick={handleUndoClear} />
      </Box>
    );
  }

  return (
    <ClearFieldFocusContainer key={col.key}>
      <TextInput
        label={col.label}
        placeholder={showEmptyChip ? '' : col.placeholder}
        fullWidth
        disabled={disabled}
        ref={isFirst ? inputRef : undefined}
        autoFocus={isFirst}
        value={(value as string) || ''}
        onChange={handleChange}
        onKeyDown={e => {
          if (e.key === 'Enter' && onEnterPress) {
            e.preventDefault();
            e.stopPropagation();
            onEnterPress();
          }
        }}
        labelSeparate
        variant="outlined"
        InputProps={showEmptyChip ? { startAdornment: emptyDisplayNode } : undefined}
      />
      <ClearFieldButton onClick={handleClear} visible={showClearButton} />
      <FieldError error={errorMessage} show={showError} />
    </ClearFieldFocusContainer>
  );
};

/**
 * Memoized editable text cell component (reads editable state from context when inside table).
 */
type TextEditableCellProps<T extends { id: number | string }> = {
  context: CellContext<T, unknown>;
  value: string;
  col: CustomColumn<T>;
  editableRowIndex?: number | null;
  editableColumnId?: string | null;
  editableState?: EditableState | null;
  onEditCell?: (context: CellContext<T, unknown>) => void;
  onClearEdit?: () => void;
  isRowEditable: boolean;
};

const TextEditableCellInner = <T extends { id: number | string }>({
  context,
  value,
  col,
  editableRowIndex: editableRowIndexProp,
  editableColumnId: editableColumnIdProp,
  editableState: editableStateProp,
  onEditCell: onEditCellProp,
  onClearEdit: onClearEditProp,
  isRowEditable,
}: TextEditableCellProps<T>) => {
  const editableCtx = useEditableContextOptional();
  const editableState = editableStateProp ?? editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? (() => undefined);
  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const onEditCell =
    onEditCellProp ??
    (() => setEditableState({ rowIndex: context.row.index, columnId: context.column.id }));
  const onClearEdit =
    onClearEditProp ?? (() => setEditableState({ rowIndex: null, columnId: null }));

  const handleChange = useCallback(
    (newValue: string) => {
      // Auto-infer: use col.id as the field name
      if (col.onUpdate) {
        // Prevent clearing required fields
        if ((!newValue || newValue.trim() === '') && 'required' in col) {
          // Show validation error message
          toast.error(t`${col.label || 'This field'} is required and cannot be empty`);
          onClearEdit();
          return;
        }

        const result = col.onUpdate(context.row.original, {
          [col.id]: newValue,
        } as Partial<T>);
        clearEditOnSuccess(result, onClearEdit);
      } else {
        onClearEdit();
      }
    },
    [col, context.row.original, onClearEdit]
  );

  // If not editable for this row, render as read-only text with ellipsis
  if (!isRowEditable) {
    return <TruncatedText>{value}</TruncatedText>;
  }

  return (
    <SharedEditableCell
      {...context}
      meta={{ editable: true, onCellEdit: onEditCell }}
      isEditable={context.row.index === editableRowIndex && context.column.id === editableColumnId}
      tooltipTitle={editableState?.rowIndex != null ? undefined : value}
      readModeEl={<TruncatedText>{value}</TruncatedText>}
      onClearEdit={onClearEdit}
    >
      <CellInput value={value || ''} onChange={handleChange} />
    </SharedEditableCell>
  );
};

const TextEditableCell = memo(TextEditableCellInner) as <T extends { id: number | string }>(
  props: TextEditableCellProps<T>
) => JSX.Element;

/**
 * Text editable renderer - unified pattern
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 */
export const textEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const editableProp = col.editable;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellStringValue(context, col.id);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      return (
        <TextEditableCell context={context} value={value} col={col} isRowEditable={isRowEditable} />
      );
    },
    meta: {
      editable: true,
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.getValue();
      return String(value ?? '');
    },
  };
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Text field type configuration
 */
export const textFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Enter ${label.toLowerCase()}...`,
  ...L,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const textReadOnly: ColumnRenderer = textReadOnlyRenderer;

/** Editable renderer with proper typing */
export const textEditable: ColumnRenderer = textEditableRenderer;
